// hooks/channel/useStationMenu.ts
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import useStationSidebarMenuQuery from '@/query/channel/menu/useStationSidebarMenuQuery';
import globalDomains from '@/utils/global-domains';
import {IconName, MainMenuItemProps, useConfirm} from '@soop-ui/soop-ui';
import {SubscribeFanModel} from '@/types/channel/menu/menu.model';
import {
  getLocalStorage,
  getUserLoginId,
  goLogin,
  isLogin,
  setLocalStorage,
} from '@/utils/main';
import {
  useStationData,
  useStationUI,
  useStationUser,
} from '@/stores/channel/station';
import {useFavoriteQuery} from '@/query/channel/board/useFavoriteQuery';
import throwToast from '@/utils/ui/toast';
import {
  useDeleteFavoriteMutation,
  useFavoriteMutation,
} from '@/query/channel/board/useFavoriteMutation';
import {ROUTE} from '@/constants/main/route';
import {useNamedNavigate} from '@/hooks/channel/useNamedNavigate';
import {useQueryClient} from '@tanstack/react-query';
import {HOME_PREFIX} from '@/query/channel/home/queryKeys';
import {CALENDAR_PREFIX} from '@/query/channel/calendar/queryKeys';
import {
  PLAYLIST_LIST_PREFIX,
  VOD_LIST_PREFIX,
  VOD_LIST_QUERY_KEY,
} from '@/query/channel/vod/queryKeys';
import {
  BOARD_QUERY_KEY,
  FAVORITE_PREFIX,
  FIX_TO_HOME_PREFIX,
  POST_LIST_PREFIX,
  REPORT_PREFIX,
} from '@/query/channel/board/queryKeys';
import {
  PRODUCT_LIST_PREFIX,
  PRODUCT_LIVE_INFO_PREFIX,
} from '@/query/channel/sooptore/queryKeys';
import {useAuthenticationQuery} from '@/query/channel/station/useStationQuery';
import {resendWebUvLogIfSamePath} from '@/utils/logger/event';
import {GUEST_QUERY_KEY} from '@/query/channel/guest/queryKeys';

export const useStationMenu = (
  /** 사이드바 열림/닫힘 상태 */
  isMenuExpanded: boolean,
) => {
  const navigate = useNavigate();
  const namedNavigate = useNamedNavigate();
  const location = useLocation();

  const {station} = useStationData();
  const {userId} = station?.detail || {};
  const {userPermissions} = useStationUser();
  const {loading} = useStationUI();
  const {isOwner, isManager} = userPermissions || {};

  const [favoriteCheckUserId, setFavoriteCheckUserId] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);

  const {mutateAsync: addFavorite} = useFavoriteMutation();
  const {mutateAsync: deleteFavorite} = useDeleteFavoriteMutation();

  const {refetch: refetchFavoriteState} = useFavoriteQuery(
    favoriteCheckUserId ?? '',
  );
  const {pathname, state} = location;
  const prevBoardNumber = Number(state?.boardNumber ?? 0);

  const queryClient = useQueryClient();

  const {openConfirm} = useConfirm();

  const handleFavoriteClick = async (userId: string) => {
    if (!isLogin()) {
      goLogin();
      return;
    }

    if (isFavorite) {
      const isOk = await openConfirm({
        message: '해당 스트리머를 즐겨찾기에서 삭제하시겠습니까?',
      });

      if (isOk) {
        await deleteFavorite({userId});
        setIsFavorite(false);
      }
    } else {
      await addFavorite({streamerId: userId || ''});
      setIsFavorite(true);
    }
  };

  const handleSearchClick = (searchUserId: string) => {
    namedNavigate(
      ROUTE.CHANNEL.SERVICE.VOD.ALL,
      {streamerId: userId || ''},
      {},
      {keyword: searchUserId, isUserSearch: true},
    );
  };

  const handleOpenChange = (userId: string) => {
    setIsFavorite(false);
    setFavoriteCheckUserId(userId);
  };

  useEffect(() => {
    const fetchFavorite = async () => {
      if (favoriteCheckUserId !== '') {
        const result = await refetchFavoriteState();
        setIsFavorite(result.data?.isFavorite || false);

        setFavoriteCheckUserId('');
      }
    };
    fetchFavorite();
  }, [favoriteCheckUserId]);

  const localStorageSubMenuOpen = getLocalStorage(
    'stationSubMenuToggle',
    'true',
  );
  const [subMenuToggle, setSubMenuToggle] = useState<boolean>(
    localStorageSubMenuOpen === 'true',
  );

  const {
    data: menu,
    isLoading,
    dataUpdatedAt,
  } = useStationSidebarMenuQuery({
    streamerId: userId ?? '',
  });

  const isAllLoaded = useMemo(
    () => !isLoading && !loading,
    [isLoading, loading],
  );

  const handleSubMenuToggle = () => {
    setSubMenuToggle((prev) => {
      setLocalStorage('stationSubMenuToggle', `${!prev}`);
      return !prev;
    });
  };

  const handleBoardMenuActive = (basePath: string, bbsId: number = 0) => {
    const {pathname, state} = location;

    const prevBoardNumber = Number(state?.boardNumber ?? 0);

    const isPost = pathname.split('/')[3] === 'post';
    const isBoard = pathname.split('/')[3] === 'board';
    const isGuest = pathname.split('/')[3] === 'guest';
    const isWrite = pathname.split('/')[4] === 'write';
    const isEdit = pathname.split('/')[5] === 'edit';
    const writeBoardNumber = pathname.split('/')[5] ?? 0;

    // 게시판 리스트 일떄,
    if (isBoard || isGuest) {
      return pathname === basePath;
    }

    //게시글 작성하기
    if (isPost && isEdit) {
      return bbsId === prevBoardNumber;
    }

    // 게시글 상세 페이지 일때
    if (isPost && !isWrite) {
      return bbsId === prevBoardNumber;
    }

    // 게시글 작성하기 페이지 일떄
    if (isPost && isWrite) {
      return false;
    }

    return false;
  };

  // 캐시 무효화 헬퍼 함수
  const invalidateCacheByPrefix = useCallback(
    (prefixes: string[]) => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const {queryKey} = query;
          return prefixes.includes(queryKey[0] as string);
        },
      });
    },
    [queryClient],
  );

  // 메뉴별 캐시 무효화 함수들
  const invalidateHomeCache = useCallback(() => {
    invalidateCacheByPrefix([HOME_PREFIX]);
  }, [invalidateCacheByPrefix]);

  const invalidateCalendarCache = useCallback(() => {
    invalidateCacheByPrefix([CALENDAR_PREFIX]);
  }, [invalidateCacheByPrefix]);

  /** VOD 탭 클릭 시 쿼리 캐시 초기화 */
  const invalidateVodCache = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: VOD_LIST_QUERY_KEY.VOD_LIST(userId!, 'all', {page: 1}),
    });
  }, [queryClient, userId]);

  /** Catch 탭 클릭 시 쿼리 캐시 초기화 */
  const invalidateCatchCache = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: VOD_LIST_QUERY_KEY.VOD_LIST(userId!, 'catch', {page: 1}),
    });
  }, [queryClient, userId]);

  /** 게시판 탭 클릭 시 쿼리 캐시 초기화 */
  const invalidatePostCache = useCallback(
    (boardNumber?: string) => {
      queryClient.invalidateQueries({
        queryKey: BOARD_QUERY_KEY.POST_LIST(userId!, {
          boardNumber: boardNumber,
        }),
      });
    },
    [queryClient, userId],
  );

  /** 방명록 탭 클릭 시 쿼리 캐시 초기화 */
  const invalidateGuestCache = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: GUEST_QUERY_KEY.GUEST_LIST(userId!),
    });
  }, [queryClient, userId]);

  const invalidateSooptoreCache = useCallback(() => {
    invalidateCacheByPrefix([PRODUCT_LIVE_INFO_PREFIX, PRODUCT_LIST_PREFIX]);
  }, [invalidateCacheByPrefix]);

  // 게시판 서브 메뉴
  const subMenuItems = useMemo(
    () =>
      menu?.boardList?.map((item, index) => {
        const bbsNo = String(item?.bbsId ?? index);
        const basePath =
          item?.boardType === 101
            ? `/station/${userId}/guest`
            : `/station/${userId}/board/${bbsNo}`;
        const isActive = handleBoardMenuActive(basePath, item?.bbsId ?? 0);
        return {
          menuName: item?.boardName || '',
          isLine: item?.boardType === 106,
          isGroup: item?.boardType === 107,
          isActive: isActive,
          path: basePath,
          onClick: () => {
            setTimeout(() => {
              resendWebUvLogIfSamePath(basePath);
              navigate(basePath);
              if (item?.boardType === 101) {
                invalidateGuestCache();
              } else {
                invalidatePostCache(bbsNo);
              }
            }, 0);
          },
        };
      }),
    [location, menu?.boardList, navigate, userId],
  );

  const allMenuItems = useMemo(() => {
    const isActive = handleBoardMenuActive(`/station/${userId}/board`, 0);
    return [
      {
        menuName: '전체 게시판',
        isLine: false,
        isGroup: false,
        isActive: isActive,
        path: `/station/${userId}/board`,
        onClick: () => {
          resendWebUvLogIfSamePath(`/station/${userId}/board`);
          navigate(`/station/${userId}/board`);
          invalidatePostCache();
        },
      },
      ...(subMenuItems || []),
    ];
  }, [subMenuItems, location, navigate, userId]);

  const isPathActive = useCallback(
    (path: string | string[]) =>
      Array.isArray(path)
        ? path.some((p) => location.pathname === p)
        : location.pathname === path,
    [location.pathname],
  );

  // 좌측 사이드 프로필 메뉴
  const profileMenuItems = useMemo<MainMenuItemProps[]>(() => {
    const isPost = pathname.split('/')[3] === 'post';
    const isEdit = pathname.split('/')[5] === 'edit';
    const isBoard = pathname.split('/')[3] === 'board';
    const isGuest = pathname.split('/')[3] === 'guest';
    const isWrite = pathname.split('/')[4] === 'write';
    const isCalendar = pathname.split('/')[3] === 'calendar';
    const boardNumber = pathname.split('/')[4] ?? 0;

    return [
      {
        isProfile: false,
        iconName: 'icoPen' as IconName,
        menuName: '글쓰기',
        path: [`/station/${userId}/post/write`],
        onClick: () => {
          if (isPost && isWrite) {
            return;
          }
          // 게시글 상세 페이지 일때
          if (isPost && !isWrite) {
            navigate(
              `/station/${userId}/post/write${prevBoardNumber ? `/${prevBoardNumber}` : ''}`,
            );
            return;
          }
          // 게시판 리스트 일떄,
          if (isBoard || isGuest) {
            navigate(
              `/station/${userId}/post/write${boardNumber ? `/${boardNumber}` : ''}`,
            );
            return;
          }
          if (isPost && isEdit) {
            navigate(`/station/${userId}/post/write`);
            return;
          }
          navigate(`/station/${userId}/post/write`);
        },
        isActive: isPost && isWrite,
      },
      {
        isProfile: false,
        iconName: 'icoCalendar-06' as IconName,
        menuName: '캘린더',
        path: [`/station/${userId}/calendar`],
        onClick: () => {
          navigate(`/station/${userId}/calendar`);
        },
        isActive: isCalendar,
      },
    ];
  }, [userId, navigate, isPathActive]);

  // 좌측 사이드 전체 메뉴
  const sidebarMenuItems = useMemo<MainMenuItemProps[]>(() => {
    const items = [
      {
        isProfile: false,
        iconName: 'icoVod' as IconName,
        menuName: 'VOD',
        path: [
          `/station/${userId}/vod`,
          `/station/${userId}/vod/review`,
          `/station/${userId}/vod/clip`,
          `/station/${userId}/vod/normal`,
          `/station/${userId}/vod/list`,
        ],
        onClick: () => {
          resendWebUvLogIfSamePath(`/station/${userId}/vod`);
          navigate(`/station/${userId}/vod`);
          invalidateVodCache();
        },
      },
      {
        isProfile: false,
        iconName: 'icoPost' as IconName,
        menuName: '게시판',
        isSubMenu: true,
        isSubMenuOpen: subMenuToggle,
        subMenuList: allMenuItems,
        onClick: () => {
          if (isMenuExpanded) {
            handleSubMenuToggle();
          } else {
            resendWebUvLogIfSamePath(`/station/${userId}/board`);
            navigate(`/station/${userId}/board`);
            invalidatePostCache();
          }
        },
      },
      {
        isProfile: false,
        iconName: 'icoCatch' as IconName,
        menuName: 'Catch',
        path: [`/station/${userId}/catch`, `/station/${userId}/catchstory`],
        onClick: () => {
          resendWebUvLogIfSamePath(`/station/${userId}/catch`);
          navigate(`/station/${userId}/catch`);
          invalidateCatchCache();
        },
      },
    ];

    if (menu?.hiddenMenu?.showSooptoreMenu) {
      items.push({
        isProfile: false,
        iconName: 'icoShop' as IconName,
        menuName: '숲토어',
        path: [`/station/${userId}/sooptore`],
        onClick: () => {
          resendWebUvLogIfSamePath(`/station/${userId}/sooptore`);
          navigate(`/station/${userId}/sooptore`);
          invalidateSooptoreCache();
        },
      });
    }
    if (isOwner && menu?.hiddenMenu?.showSupportersMenu) {
      items.push({
        isProfile: false,
        iconName: 'icoPostSp' as IconName,
        menuName: 'SP 서포터즈 게시판',
        path: [globalDomains.SUPPORTERS_AFREECATV],
        onClick: () => window.open(globalDomains.SUPPORTERS_AFREECATV),
      });
    }
    if (isOwner && menu?.hiddenMenu?.showBestMenu) {
      items.push({
        isProfile: false,
        iconName: 'icoPostBest' as IconName,
        menuName: '베스트 스트리머',
        path: [globalDomains.BESTBJ_AFREECATV],
        onClick: () => window.open(globalDomains.BESTBJ_AFREECATV),
      });
    }
    if (isOwner && menu?.hiddenMenu?.showPartnerMenu) {
      items.push({
        isProfile: false,
        iconName: 'icoPostPartner' as IconName,
        menuName: '파트너 스트리머',
        path: [globalDomains.PARTNERBJ_AFREECATV],
        onClick: () => window.open(globalDomains.PARTNERBJ_AFREECATV),
      });
    }

    return items.map((item) => {
      const isParentActive = item.subMenuList?.some((s) => s.isActive);

      const isSelfActive = !!item.path && isPathActive(item.path);

      return {
        ...item,
        isActive: isParentActive ?? isSelfActive ?? false,
      };
    });
  }, [
    userId,
    subMenuToggle,
    allMenuItems,
    menu,
    navigate,
    isMenuExpanded,
    isPathActive,
  ]);
  // 좌측 사이드 세팅 메뉴
  const settingMenuItems = useMemo<MainMenuItemProps[]>(() => {
    const items: MainMenuItemProps[] = [];

    if (!isMenuExpanded) {
      items.push({
        isProfile: false,
        iconName: 'icoSetting' as IconName,
        menuName: '방송국 관리',
        path: [`/station/${userId}/setting`],
        onClick: () => {
          navigate(`/station/${userId}/setting`);
        },
      });
    }
    return items;
  }, [isOwner, isManager, isMenuExpanded, userId, navigate, isPathActive]);

  const sidebarTopFanList = useMemo(() => {
    return (
      menu?.fanList?.map((fan, index) => {
        let grade =
          index === 0 ? '회장' : index === 1 ? '부회장' : `${index + 1}등`;
        return {
          nickname: fan.userNickname,
          userId: fan.userId,
          isMe: fan.userId === getUserLoginId(),
          isFavorite: isFavorite,
          onMenuItemClick: (open: boolean) => handleOpenChange(fan.userId),
          onFavoriteClick: (e: any) => handleFavoriteClick(fan.userId),
          onSearchClick: (e: any) => handleSearchClick(fan.userId),
          grade,
        };
      }) ?? []
    );
  }, [menu?.fanList, isFavorite]);

  const sidebarSubscribeGiftList = useMemo(() => {
    return (
      menu?.subscribeGiftFans?.map((subscribe: SubscribeFanModel, index) => ({
        nickname: subscribe.userNickname,
        userId: subscribe.userId,
        isMe: subscribe.userId === getUserLoginId(),
        isFavorite: isFavorite,
        onMenuItemClick: (open: boolean) => handleOpenChange(subscribe.userId),
        onFavoriteClick: (e: any) => handleFavoriteClick(subscribe.userId),
        onSearchClick: (e: any) => handleSearchClick(subscribe.userId),
        grade: `${index + 1}등`,
      })) ?? []
    );
  }, [menu?.subscribeGiftFans, isFavorite]);

  const sidebarSubscriberList = useMemo(() => {
    return (
      menu?.subscribeAccFans?.map((subscribe, index) => ({
        nickname: subscribe.userNickname,
        userId: subscribe.userId,
        isMe: subscribe.userId === getUserLoginId(),
        isFavorite: isFavorite,
        onMenuItemClick: (open: boolean) => handleOpenChange(subscribe.userId),
        onFavoriteClick: (e: any) => handleFavoriteClick(subscribe.userId),
        onSearchClick: (e: any) => handleSearchClick(subscribe.userId),
        grade: `${index + 1}등`,
      })) ?? []
    );
  }, [menu?.subscribeAccFans, isFavorite]);

  return {
    userId,
    isOwner,
    isManager,
    menu,
    isLoading,
    loading,
    isAllLoaded,
    dataUpdatedAt,
    sidebarMenuItems,
    profileMenuItems,
    settingMenuItems,
    sidebarTopFanList,
    sidebarSubscribeGiftList,
    sidebarSubscriberList,
  };
};
